import { useMutation } from '@tanstack/react-query';

import {
  useInvalidateChannelById,
  useInvalidateChannelsList,
} from '@/entities/channel';
import { ApiError } from '@/shared/api/ApiError';
import {
  type AvitoConnectResponse,
  connectAvito,
  disconnectAvito,
} from '@/shared/api/channels/avito';
import { useNotifications } from '@/shared/ui/Notifications';

interface AvitoConnectInput {
  channelId: string;
  clientId: string;
  clientSecret: string;
  name?: string;
}

export function useAvitoConnect() {
  const invalidateChannelsList = useInvalidateChannelsList();
  const invalidateChannelById = useInvalidateChannelById();
  const { addNotification } = useNotifications();

  const mutation = useMutation<
    AvitoConnectResponse,
    ApiError,
    AvitoConnectInput
  >({
    mutationFn: ({ channelId, clientId, clientSecret, name }) =>
      connectAvito(channelId, {
        client_id: clientId,
        client_secret: clientSecret,
        name: name?.trim() || undefined,
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateChannelsList(),
        invalidateChannelById(variables.channelId),
      ]);
    },
    onError: (error) => {
      addNotification({
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  return {
    connect: (input: AvitoConnectInput) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    reset: mutation.reset,
  };
}

export function useAvitoDisconnect() {
  const invalidateChannelsList = useInvalidateChannelsList();
  const invalidateChannelById = useInvalidateChannelById();
  const { addNotification } = useNotifications();

  const mutation = useMutation<void, ApiError, string>({
    mutationFn: (channelId) => disconnectAvito(channelId),
    onSuccess: async (_, channelId) => {
      await Promise.all([
        invalidateChannelsList(),
        invalidateChannelById(channelId),
      ]);
    },
    onError: (error) => {
      addNotification({
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  return {
    disconnect: (channelId: string, onDisconnected?: () => void) =>
      mutation.mutateAsync(channelId, { onSuccess: onDisconnected }),
    isPending: mutation.isPending,
  };
}
